import { useState, useCallback, useEffect } from 'react';
import {
  APIProvider,
  Map,
  AdvancedMarker,
  Pin,
  InfoWindow,
  useMap,
  useMapsLibrary,
} from '@vis.gl/react-google-maps';
import {
  Box,
  Typography,
  Paper,
  alpha,
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import StorefrontIcon from '@mui/icons-material/Storefront';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import { palette } from '../theme/colors';

/* eslint-disable @typescript-eslint/no-explicit-any */

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';
const GOOGLE_MAPS_MAP_ID = process.env.REACT_APP_GOOGLE_MAPS_MAP_ID || 'DEMO_MAP_ID';

const defaultCenter = {
  lat: 36.7538, // Algiers, Algeria default
  lng: 3.0588,
};

export interface MapMarker {
  id: string;
  position: { lat: number; lng: number };
  title?: string;
  label?: string;
  icon?: 'pickup' | 'delivery' | 'driver' | 'customer' | 'store' | 'warehouse';
  info?: React.ReactNode;
}

interface GoogleMapsWrapperProps {
  markers?: MapMarker[];
  center?: { lat: number; lng: number };
  zoom?: number;
  height?: string | number;
  showDirections?: boolean;
  origin?: { lat: number; lng: number };
  destination?: { lat: number; lng: number };
  waypoints?: { lat: number; lng: number }[];
  onMarkerClick?: (marker: MapMarker) => void;
  onMapClick?: (position: { lat: number; lng: number }) => void;
  darkMode?: boolean;
  fitBounds?: boolean;
}

// Get Pin colors based on marker type
const getPinColors = (type: MapMarker['icon']) => {
  const colorMap: Record<string, { background: string; borderColor: string; glyphColor: string }> = {
    pickup: { background: palette.primary, borderColor: '#1565c0', glyphColor: '#ffffff' },
    delivery: { background: palette.info, borderColor: '#0277bd', glyphColor: '#ffffff' },
    driver: { background: palette.success, borderColor: '#2e7d32', glyphColor: '#ffffff' },
    customer: { background: '#F59E0B', borderColor: '#d97706', glyphColor: '#ffffff' },
    store: { background: palette.primary, borderColor: '#1565c0', glyphColor: '#ffffff' },
    warehouse: { background: '#6b7280', borderColor: '#4b5563', glyphColor: '#ffffff' },
  };
  return colorMap[type || 'customer'] || colorMap.customer;
};

// Component to render directions/route on the map
const Directions: React.FC<{
  origin: { lat: number; lng: number };
  destination: { lat: number; lng: number };
  waypoints?: { lat: number; lng: number }[];
}> = ({ origin, destination, waypoints = [] }) => {
  const map = useMap();
  const routesLibrary = useMapsLibrary('routes');
  const [directionsService, setDirectionsService] = useState<google.maps.DirectionsService | null>(null);
  const [directionsRenderer, setDirectionsRenderer] = useState<google.maps.DirectionsRenderer | null>(null);

  // Initialize DirectionsService and DirectionsRenderer
  useEffect(() => {
    if (!routesLibrary || !map) return;

    const service = new routesLibrary.DirectionsService();
    const renderer = new routesLibrary.DirectionsRenderer({
      map,
      suppressMarkers: true, // We use our own markers
      polylineOptions: {
        strokeColor: palette.primary,
        strokeWeight: 4,
        strokeOpacity: 0.8,
      },
    });

    setDirectionsService(service);
    setDirectionsRenderer(renderer);

    return () => {
      renderer.setMap(null);
    };
  }, [routesLibrary, map]);

  // Calculate and display route
  useEffect(() => {
    if (!directionsService || !directionsRenderer || !origin || !destination) return;

    const waypointsFormatted = waypoints.map((wp) => ({
      location: new google.maps.LatLng(wp.lat, wp.lng),
      stopover: true,
    }));

    directionsService
      .route({
        origin: new google.maps.LatLng(origin.lat, origin.lng),
        destination: new google.maps.LatLng(destination.lat, destination.lng),
        waypoints: waypointsFormatted,
        optimizeWaypoints: false, // We've already optimized
        travelMode: google.maps.TravelMode.DRIVING,
      })
      .then((response) => {
        directionsRenderer.setDirections(response);
      })
      .catch((error) => {
        console.error('Directions request failed:', error);
      });
  }, [directionsService, directionsRenderer, origin, destination, waypoints]);

  return null;
};

// Inner component that uses the Map context for fitBounds
const MapContent: React.FC<{
  markers: MapMarker[];
  fitBounds: boolean;
  origin?: { lat: number; lng: number };
  destination?: { lat: number; lng: number };
  selectedMarker: MapMarker | null;
  setSelectedMarker: (marker: MapMarker | null) => void;
  onMarkerClick?: (marker: MapMarker) => void;
}> = ({ markers, fitBounds, origin, destination, selectedMarker, setSelectedMarker, onMarkerClick }) => {
  const map = useMap();

  // Fit bounds to markers
  useEffect(() => {
    if (!map || markers.length === 0 || !fitBounds) return;

    const bounds = new google.maps.LatLngBounds();
    markers.forEach((marker) => {
      bounds.extend(marker.position);
    });

    if (origin) bounds.extend(origin);
    if (destination) bounds.extend(destination);

    map.fitBounds(bounds);

    // Limit max zoom after fitBounds
    const listener = google.maps.event.addListenerOnce(map, 'idle', () => {
      const currentZoom = map.getZoom();
      if (currentZoom && currentZoom > 16) {
        map.setZoom(16);
      }
    });

    return () => {
      google.maps.event.removeListener(listener);
    };
  }, [map, markers, origin, destination, fitBounds]);

  const handleMarkerClick = useCallback(
    (marker: MapMarker) => {
      setSelectedMarker(marker);
      onMarkerClick?.(marker);
    },
    [onMarkerClick, setSelectedMarker]
  );

  return (
    <>
      {markers.map((marker) => {
        const colors = getPinColors(marker.icon);

        // Use custom icons for store and warehouse
        if (marker.icon === 'store' || marker.icon === 'warehouse') {
          const IconComponent = marker.icon === 'store' ? StorefrontIcon : WarehouseIcon;
          return (
            <AdvancedMarker
              key={marker.id}
              position={marker.position}
              title={marker.title}
              onClick={() => handleMarkerClick(marker)}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  backgroundColor: colors.background,
                  border: `3px solid ${colors.borderColor}`,
                  boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                  cursor: 'pointer',
                  transition: 'transform 0.2s',
                  '&:hover': {
                    transform: 'scale(1.1)',
                  },
                }}
              >
                <IconComponent sx={{ color: colors.glyphColor, fontSize: 22 }} />
              </Box>
            </AdvancedMarker>
          );
        }

        // Use Pin for other marker types, with optional label/number
        return (
          <AdvancedMarker
            key={marker.id}
            position={marker.position}
            title={marker.title}
            onClick={() => handleMarkerClick(marker)}
          >
            {marker.label ? (
              // Custom marker with order number
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  backgroundColor: colors.background,
                  border: `3px solid ${colors.borderColor}`,
                  boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                  cursor: 'pointer',
                  transition: 'transform 0.2s',
                  '&:hover': {
                    transform: 'scale(1.1)',
                  },
                }}
              >
                <Typography
                  sx={{
                    color: colors.glyphColor,
                    fontSize: 14,
                    fontWeight: 700,
                    lineHeight: 1,
                  }}
                >
                  {marker.label}
                </Typography>
              </Box>
            ) : (
              <Pin
                background={colors.background}
                borderColor={colors.borderColor}
                glyphColor={colors.glyphColor}
                scale={1.2}
              />
            )}
          </AdvancedMarker>
        );
      })}

      {selectedMarker && selectedMarker.info && (
        <InfoWindow
          position={selectedMarker.position}
          onCloseClick={() => setSelectedMarker(null)}
        >
          <Box sx={{ minWidth: 150, p: 1 }}>{selectedMarker.info}</Box>
        </InfoWindow>
      )}
    </>
  );
};

export const GoogleMapsWrapper: React.FC<GoogleMapsWrapperProps> = ({
  markers = [],
  center = defaultCenter,
  zoom = 12,
  height = 400,
  showDirections = false,
  origin,
  destination,
  waypoints = [],
  onMarkerClick,
  onMapClick,
  darkMode = false,
  fitBounds = true,
}) => {
  const [selectedMarker, setSelectedMarker] = useState<MapMarker | null>(null);

  const handleMapClick = useCallback(
    (e: any) => {
      if (e.detail?.latLng) {
        onMapClick?.({
          lat: e.detail.latLng.lat,
          lng: e.detail.latLng.lng,
        });
      }
      setSelectedMarker(null);
    },
    [onMapClick]
  );

  if (!GOOGLE_MAPS_API_KEY) {
    return (
      <Paper
        elevation={0}
        sx={{
          height,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: darkMode ? alpha('#000', 0.3) : alpha('#000', 0.05),
          borderRadius: 2,
          border: `1px dashed ${palette.textMuted}`,
        }}
      >
        <LocationOnIcon
          sx={{ fontSize: 48, color: palette.textMuted, mb: 2 }}
        />
        <Typography variant="h6" color="textSecondary" gutterBottom>
          Google Maps Not Configured
        </Typography>
        <Typography variant="body2" color="textSecondary" textAlign="center">
          Please set REACT_APP_GOOGLE_MAPS_API_KEY in your environment variables
        </Typography>
      </Paper>
    );
  }

  // Use a dark map ID or default based on darkMode
  // Note: For custom styling, you need to create a Map ID in Google Cloud Console
  const mapId = darkMode ? GOOGLE_MAPS_MAP_ID : GOOGLE_MAPS_MAP_ID;

  return (
    <Box sx={{ height, position: 'relative', borderRadius: 2, overflow: 'hidden' }}>
      <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
        <Map
          defaultCenter={center}
          defaultZoom={zoom}
          mapId={mapId}
          onClick={handleMapClick}
          style={{ width: '100%', height: '100%' }}
          disableDefaultUI={false}
          zoomControl={true}
          streetViewControl={false}
          mapTypeControl={false}
          fullscreenControl={true}
          colorScheme={darkMode ? 'DARK' : 'LIGHT'}
        >
          <MapContent
            markers={markers}
            fitBounds={fitBounds}
            origin={origin}
            destination={destination}
            selectedMarker={selectedMarker}
            setSelectedMarker={setSelectedMarker}
            onMarkerClick={onMarkerClick}
          />
          {showDirections && origin && destination && (
            <Directions
              origin={origin}
              destination={destination}
              waypoints={waypoints}
            />
          )}
        </Map>
      </APIProvider>
    </Box>
  );
};

export default GoogleMapsWrapper;
